import { getCompletitionData } from '@src/packages/utils/functions/triggers';
import type { ExecutionStatesByExecutionId } from '@src/packages/utils/hooks/useExecutionStatesRt';

import type { ComplianceByUser, ComplianceCounts, ExecutionComplianceType } from '../types';

interface ExecutionMember {
  id: string;
}

export interface ComplianceExecution {
  id: string;
  schedule?: {
    type?: string;
    members?: {
      users?: ExecutionMember[];
      groups?: { users?: ExecutionMember[] } | Array<{ users?: ExecutionMember[] }>;
    };
  };
}

interface TriggerDoc {
  expectedExecutions?: number;
  finishedExecutions?: number;
}

const COMPLIANCE_TYPES: readonly ExecutionComplianceType[] = [
  'schedule',
  'task',
  'checkpoint_route',
];

const isComplianceType = (value: string | undefined): value is ExecutionComplianceType =>
  COMPLIANCE_TYPES.includes(value as ExecutionComplianceType);

const zeroCompliance = (): Record<ExecutionComplianceType, ComplianceCounts> => ({
  schedule: { finished: 0, expected: 0 },
  task: { finished: 0, expected: 0 },
  checkpoint_route: { finished: 0, expected: 0 },
});

/** All member uids of an execution (direct users + group users). */
export const getExecutionMemberIds = (execution: ComplianceExecution): string[] => {
  const members = execution.schedule?.members;
  const direct = members?.users ?? [];
  const groups = members?.groups;
  const groupUsers = Array.isArray(groups)
    ? groups.flatMap(group => group?.users ?? [])
    : groups?.users ?? [];
  return Array.from(new Set([...direct, ...groupUsers].map(member => member.id).filter(Boolean)));
};

/**
 * Per-user trigger completion bucketed by execution kind, plus whether any of their
 * executions of any kind has stopped.
 *
 * `stalled` reads `currentState` from `statesByExecutionId` — the same `execution_states`
 * source every other dashboard uses — rather than the backend `isDelayed` flag (written
 * once as `false`, never updated) or the execution doc's own denormalized `status` field,
 * which nothing else in the app trusts either. It spans every execution kind: a stalled
 * task used to be invisible here even though the work-order and operational-panel views
 * already showed it as stalled. See docs/adr/0001-single-execution-state-vocabulary.md.
 */
export const buildComplianceByUser = (
  executions: readonly ComplianceExecution[],
  statesByExecutionId: ExecutionStatesByExecutionId,
  triggersByExecution: Readonly<Record<string, TriggerDoc[] | undefined>>,
): ComplianceByUser => {
  const byUser: ComplianceByUser = {};

  executions.forEach(execution => {
    const triggers = triggersByExecution[execution.id];
    const completion = triggers ? getCompletitionData(triggers) : null;
    const isStalled = statesByExecutionId[execution.id]?.currentState === 'stalled';
    const type = execution.schedule?.type;

    getExecutionMemberIds(execution).forEach(uid => {
      const current = byUser[uid] ?? { stalled: false, compliance: zeroCompliance() };
      const bucket = isComplianceType(type) ? current.compliance[type] : null;

      byUser[uid] = {
        stalled: current.stalled || isStalled,
        compliance: bucket
          ? {
              ...current.compliance,
              [type as ExecutionComplianceType]: {
                finished: bucket.finished + (completion?.finishedExecutions ?? 0),
                expected: bucket.expected + (completion?.expectedExecutions ?? 0),
              },
            }
          : current.compliance,
      };
    });
  });

  return byUser;
};
